import React from 'react';
import { Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip as MuiTooltip, useTheme, useMediaQuery } from '@mui/material';
import { HeatmapData } from '../../../types/consolidatedReport.types';

interface HeatmapTableProps {
  data: HeatmapData;
}

const HeatmapTable: React.FC<HeatmapTableProps> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getColor = (valor: number | null) => {
    if (valor === null) return 'text.disabled';
    if (valor >= 85) return '#4caf50';
    if (valor >= 60) return '#ff9800';
    return '#f44336';
  };

  const getBackgroundColor = (valor: number | null) => {
    if (valor === null) return 'rgba(0, 0, 0, 0.04)';
    if (valor >= 85) return 'rgba(76, 175, 80, 0.1)';
    if (valor >= 60) return 'rgba(255, 152, 0, 0.1)';
    return 'rgba(244, 67, 54, 0.1)';
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: isMobile ? 2 : 3,
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
        Mapa de Calor - Auditorías por Categoría
      </Typography>
      
      {isMobile && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Desliza horizontalmente para ver todas las categorías →
        </Typography>
      )}

      <TableContainer 
        sx={{ 
          maxHeight: isMobile ? 400 : 600,
          '&::-webkit-scrollbar': {
            height: 8,
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <Table size={isMobile ? 'small' : 'small'} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 700, 
                  minWidth: isMobile ? 120 : 200, 
                  maxWidth: isMobile ? 120 : 250,
                  fontSize: isMobile ? 11 : 14,
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 3
                }}
              >
                Auditoría
              </TableCell>
              {data.categorias.map((cat, i) => (
                <TableCell 
                  key={i} 
                  align="center" 
                  sx={{ 
                    fontWeight: 700, 
                    minWidth: isMobile ? 60 : 80,
                    fontSize: isMobile ? 10 : 14
                  }}
                >
                  {isMobile && cat.length > 8 ? cat.substring(0, 8) + '...' : cat}
                </TableCell>
              ))}
              <TableCell 
                align="center" 
                sx={{ 
                  fontWeight: 700, 
                  minWidth: isMobile ? 60 : 80,
                  fontSize: isMobile ? 11 : 14,
                  position: 'sticky',
                  right: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 3
                }}
              >
                Prom.
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.audits.map((auditoria) => (
              <TableRow key={auditoria.id} hover>
                <TableCell 
                  sx={{ 
                    fontSize: isMobile ? 10 : 12, 
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    minWidth: isMobile ? 120 : 200,
                    maxWidth: isMobile ? 120 : 250,
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 2
                  }}
                >
                  {auditoria.label}
                </TableCell>
                {auditoria.scores.map((puntaje, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align="center"
                    sx={{
                      bgcolor: getBackgroundColor(puntaje),
                      color: puntaje === null ? 'text.disabled' : getColor(puntaje),
                      fontWeight: puntaje === null ? 400 : 600,
                      fontSize: isMobile ? 11 : 13,
                      fontStyle: puntaje === null ? 'italic' : 'normal'
                    }}
                  >
                    <MuiTooltip
                      title={
                        puntaje === null
                          ? `${data.categorias[colIndex]}: No se evalúa en este formulario`
                          : `${data.categorias[colIndex]}: ${puntaje}%`
                      }
                    >
                      <span>{puntaje === null ? 'N/E' : puntaje}</span>
                    </MuiTooltip>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    bgcolor: getBackgroundColor(auditoria.average),
                    color: getColor(auditoria.average),
                    fontWeight: 700,
                    fontSize: isMobile ? 12 : 14,
                    position: 'sticky',
                    right: 0,
                    backgroundColor: getBackgroundColor(auditoria.average),
                    zIndex: 2
                  }}
                >
                  {auditoria.average}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default HeatmapTable;
